import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listAdminUsers,
  getAdminUserDetail,
  updateAdminUserSupport,
  listAdminAuditLog,
} from "@/lib/admin.users.functions";
import {
  countOpenSignalsByUser,
  listAccountSignals,
  markSignalReviewed,
  type AccountSignalRow,
} from "@/lib/admin.signals.functions";
import { toast } from "sonner";
import { InstitutionPicker, type InstitutionValue } from "@/components/InstitutionPicker";
import { bulkSetUserFlag, bulkAddSupportNote, exportAdminCsv } from "@/lib/admin.reports.functions";

export const Route = createFileRoute("/admin/users")({
  component: AdminUsersPage,
});

const SUPPORT_NOTE_TEMPLATES: { id: string; label: string; body: string }[] = [
  {
    id: "verify-institution",
    label: "Verify institution",
    body: "Please confirm your university and course on your profile so we can verify your account.",
  },
  {
    id: "integrity-check",
    label: "Integrity check follow-up",
    body: "We have flagged unusual activity on this account. Please reply to confirm recent activity was genuinely yours.",
  },
  {
    id: "welcome",
    label: "Welcome and onboarding",
    body: "Welcome to Tiger's Eye. Reach out if you need help submitting your first draft for critique.",
  },
  {
    id: "marker-feedback-thanks",
    label: "Thanks for marker feedback",
    body: "Thanks for sharing marker feedback. It helps us calibrate Tiger's Eye and supports future cohorts.",
  },
];


function AdminUsersPage() {
  const [q, setQ] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const pageSize = 50;
  const qc = useQueryClient();

  const listFn = useServerFn(listAdminUsers);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users", q, flaggedOnly, page],
    queryFn: () =>
      listFn({ data: { q, flaggedOnly, limit: pageSize, offset: page * pageSize } }),
  });

  const countsFn = useServerFn(countOpenSignalsByUser);
  const userIds = (data?.users ?? []).map((u) => u.id);
  const { data: signalCounts } = useQuery({
    queryKey: ["admin", "user-signal-counts", userIds.join(",")],
    queryFn: () => countsFn({ data: { userIds } }),
    enabled: userIds.length > 0,
  });

  const bulkFlagFn = useServerFn(bulkSetUserFlag);
  const bulkFlag = useMutation({
    mutationFn: (flagged: boolean) =>
      bulkFlagFn({ data: { userIds: Array.from(selectedIds), flagged } }),
    onSuccess: ({ updated, skippedSelf }) => {
      toast.success(
        `Updated ${updated} user${updated === 1 ? "" : "s"}${
          skippedSelf > 0 ? ` (skipped self)` : ""
        }`,
      );
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkNoteFn = useServerFn(bulkAddSupportNote);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteMode, setNoteMode] = useState<"append" | "replace">("append");
  const bulkNote = useMutation({
    mutationFn: () =>
      bulkNoteFn({
        data: { userIds: Array.from(selectedIds), note: noteText, mode: noteMode },
      }),
    onSuccess: ({ updated }) => {
      toast.success(`Note added to ${updated} user${updated === 1 ? "" : "s"}`);
      setNoteOpen(false);
      setNoteText("");
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const exportFn = useServerFn(exportAdminCsv);
  const exportSelected = useMutation({
    mutationFn: () =>
      exportFn({ data: { kind: "users", userIds: Array.from(selectedIds) } }),
    onSuccess: ({ filename, csv, rows }) => {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${rows} row${rows === 1 ? "" : "s"}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const allOnPageSelected =
    (data?.users.length ?? 0) > 0 && data!.users.every((u) => selectedIds.has(u.id));
  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const u of data!.users) next.delete(u.id);
      } else {
        for (const u of data!.users) next.add(u.id);
      }
      return next;
    });
  };
  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });


  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-2">Accounts</p>
        <h2 className="font-serif text-2xl text-primary mb-4">Signed-up users</h2>
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <input
            type="search"
            placeholder="Search name, email, institution…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="border border-border bg-background px-3 py-2 text-sm w-80 max-w-full"
          />
          <label className="flex items-center gap-2 text-sm text-ink-secondary">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
            />
            Flagged only
          </label>
          {data && (
            <span className="text-xs text-ink-tertiary ml-auto font-mono">
              {data.users.length} of {data.total} shown
            </span>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 border border-primary/40 bg-primary/5">
            <span className="text-sm font-mono">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={() => bulkFlag.mutate(true)}
              disabled={bulkFlag.isPending}
              className="text-xs px-3 py-1.5 border border-border bg-background hover:bg-muted disabled:opacity-40"
            >
              Flag
            </button>
            <button
              type="button"
              onClick={() => bulkFlag.mutate(false)}
              disabled={bulkFlag.isPending}
              className="text-xs px-3 py-1.5 border border-border bg-background hover:bg-muted disabled:opacity-40"
            >
              Unflag
            </button>
            <button
              type="button"
              onClick={() => exportSelected.mutate()}
              disabled={exportSelected.isPending}
              className="text-xs px-3 py-1.5 border border-border bg-background hover:bg-muted disabled:opacity-40"
            >
              {exportSelected.isPending ? "Exporting…" : "Export CSV"}
            </button>
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="text-xs px-3 py-1.5 border border-border bg-background hover:bg-muted"
            >
              Add support note…
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-ink-tertiary hover:text-primary ml-auto"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
      {error && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load users"}
        </p>
      )}

      {data && (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-ink-tertiary">
              <tr>
                <th className="p-3 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={allOnPageSelected}
                    onChange={togglePage}
                  />
                </th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Institution</th>
                <th className="text-right p-3">Reviews</th>
                <th className="text-left p-3">Joined</th>
                <th className="text-left p-3">Last seen</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                  onClick={() => setSelectedId(u.id)}
                >
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${u.email ?? u.id}`}
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleOne(u.id)}
                    />
                  </td>
                  <td className="p-3">
                    {u.flagged && (
                      <span className="inline-block w-2 h-2 rounded-full bg-destructive mr-2" />
                    )}
                    {u.preferred_name || u.display_name || "—"}
                    {signalCounts?.counts?.[u.id] ? (
                      <span
                        className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-mono bg-amber-200 text-amber-900"
                        title="Open integrity signals"
                      >
                        {signalCounts.counts[u.id]} signal{signalCounts.counts[u.id] === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3 font-mono text-xs">{u.email ?? "—"}</td>
                  <td className="p-3">{u.institution_name ?? "—"}</td>
                  <td className="p-3 text-right font-mono">{u.review_count}</td>
                  <td className="p-3 text-xs text-ink-tertiary">
                    {new Date(u.created_at).toLocaleDateString("en-GB")}
                  </td>
                  <td className="p-3 text-xs text-ink-tertiary">
                    {u.last_seen_at
                      ? new Date(u.last_seen_at).toLocaleDateString("en-GB")
                      : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      className="text-xs text-primary underline underline-offset-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(u.id);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {data.users.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-ink-tertiary text-sm">
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > pageSize && (
        <div className="flex items-center justify-between text-xs font-mono text-ink-tertiary">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1.5 border border-border disabled:opacity-40"
          >
            ← Prev
          </button>
          <span>
            Page {page + 1} of {Math.max(1, Math.ceil(data.total / pageSize))}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * pageSize >= data.total}
            className="px-3 py-1.5 border border-border disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}

      {selectedId && (
        <UserDetailDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {noteOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setNoteOpen(false)}
        >
          <div
            className="bg-background border border-border w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="overline mb-1">Bulk action</p>
            <h3 className="font-serif text-xl text-primary mb-1">
              Add support note to {selectedIds.size} user{selectedIds.size === 1 ? "" : "s"}
            </h3>
            <p className="text-xs text-ink-tertiary mb-4">
              The note is timestamped and attributed to your admin ID. Visible to admins only.
            </p>

            <label className="block text-xs text-ink-tertiary mb-3">
              <span className="overline block mb-1">Template (optional)</span>
              <select
                value=""
                onChange={(e) => {
                  const tpl = SUPPORT_NOTE_TEMPLATES.find((t) => t.id === e.target.value);
                  if (tpl) setNoteText(tpl.body);
                }}
                className="w-full border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— Insert a template —</option>
                {SUPPORT_NOTE_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={6}
              maxLength={2000}
              placeholder="e.g. Reached out via email re: institution verification."
              className="w-full border border-border bg-background p-2 text-sm font-mono mb-3"
            />


            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="bulk-note-mode"
                  checked={noteMode === "append"}
                  onChange={() => setNoteMode("append")}
                />
                Append to existing
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="bulk-note-mode"
                  checked={noteMode === "replace"}
                  onChange={() => setNoteMode("replace")}
                />
                Replace existing
              </label>
              <span className="ml-auto text-xs font-mono text-ink-tertiary">
                {noteText.length} / 2000
              </span>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setNoteOpen(false)}
                className="text-xs px-3 py-2 border border-border hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => bulkNote.mutate()}
                disabled={bulkNote.isPending || noteText.trim().length === 0}
                className="text-xs px-3 py-2 bg-primary text-primary-foreground disabled:opacity-50"
              >
                {bulkNote.isPending ? "Saving…" : "Apply to selected"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserDetailDrawer({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const detailFn = useServerFn(getAdminUserDetail);
  const updateFn = useServerFn(updateAdminUserSupport);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => detailFn({ data: { userId } }),
  });

  const [notes, setNotes] = useState<string>("");
  const [flagged, setFlagged] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [preferredName, setPreferredName] = useState<string>("");
  const [institution, setInstitution] = useState<InstitutionValue>(null);
  const [hydrated, setHydrated] = useState(false);

  if (data && !hydrated) {
    setNotes(data.profile.support_notes ?? "");
    setFlagged(data.profile.flagged ?? false);
    setDisplayName(data.profile.display_name ?? "");
    setPreferredName(data.profile.preferred_name ?? "");
    if (data.profile.institution_id && data.profile.institution_name) {
      setInstitution({
        kind: "registry",
        id: data.profile.institution_id,
        name: data.profile.institution_name,
      });
    } else if (data.profile.institution_name) {
      setInstitution({ kind: "free", name: data.profile.institution_name });
    } else {
      setInstitution(null);
    }
    setHydrated(true);
  }

  const save = useMutation({
    mutationFn: () =>
      updateFn({
        data: {
          userId,
          support_notes: notes,
          flagged,
          display_name: displayName,
          preferred_name: preferredName,
          institution_name: institution?.name ?? null,
          institution_id: institution?.kind === "registry" ? institution.id : null,
        },
      }),

    onSuccess: () => {
      toast.success("User details updated");
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", userId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex justify-end"
      onClick={onClose}
    >
      <div
        className="bg-background border-l border-border w-full max-w-xl h-full overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between mb-6">
          <p className="overline">User detail</p>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ink-tertiary hover:text-primary"
          >
            Close ✕
          </button>
        </div>

        {isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}

        {data && (
          <>
            <h3 className="font-serif text-2xl text-primary mb-1">
              {preferredName || displayName || "(no name)"}
            </h3>
            <p className="font-mono text-xs text-ink-secondary mb-4">{data.profile.email}</p>

            <div className="grid grid-cols-2 gap-3 text-sm mb-6">
              <label className="block">
                <span className="overline block mb-1">Display name</span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={120}
                  className="w-full border border-border bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="overline block mb-1">Preferred name</span>
                <input
                  type="text"
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                  maxLength={120}
                  className="w-full border border-border bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <div className="col-span-2">
                <span className="overline block mb-1">Institution</span>
                <InstitutionPicker
                  value={institution}
                  onChange={setInstitution}
                  emailDomainHint={data.profile.email?.split("@")[1] ?? null}
                />
              </div>

              <div>
                <p className="overline">Joined</p>
                <p className="text-xs">
                  {new Date(data.profile.created_at).toLocaleString("en-GB")}
                </p>
              </div>
              <div>
                <p className="overline">Last seen</p>
                <p className="text-xs">
                  {data.profile.last_seen_at
                    ? new Date(data.profile.last_seen_at).toLocaleString("en-GB")
                    : "—"}
                </p>
              </div>
              <div className="col-span-2">
                <p className="overline">User ID</p>
                <p className="font-mono text-xs break-all">{data.profile.id}</p>
              </div>
            </div>

            <div className="border-t border-border pt-4 mb-6">
              <label className="flex items-center gap-2 text-sm mb-3">
                <input
                  type="checkbox"
                  checked={flagged}
                  onChange={(e) => setFlagged(e.target.checked)}
                />
                Flag this account for attention
              </label>

              <label className="block text-sm">
                <span className="overline block mb-1">Internal support notes</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  className="w-full border border-border bg-background p-2 text-sm font-mono"
                  placeholder="Visible to admins only. Record context, contact attempts, decisions…"
                />
              </label>

              <button
                type="button"
                onClick={() => save.mutate()}
                disabled={save.isPending}
                className="mt-3 bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save changes"}
              </button>
            </div>


            <div>
              <p className="overline mb-2">Recent reviews ({data.reviews.length})</p>
              <ul className="border border-border divide-y divide-border bg-card">
                {data.reviews.map((r) => (
                  <li key={r.id} className="p-3 text-sm">
                    <div className="flex justify-between items-baseline">
                      <span className="text-primary">{r.title ?? "(untitled)"}</span>
                      <span className="font-mono text-xs text-ink-tertiary">{r.status}</span>
                    </div>
                    <p className="text-xs text-ink-tertiary mt-1">
                      {new Date(r.created_at).toLocaleString("en-GB")}
                    </p>
                  </li>
                ))}
                {data.reviews.length === 0 && (
                  <li className="p-4 text-center text-ink-tertiary text-sm">
                    No reviews yet.
                  </li>
                )}
              </ul>
            </div>

            <div className="mt-6">
              <UserSignalsSection userId={userId} />
            </div>

            <div className="mt-6">
              <UserAuditSection userId={userId} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UserAuditSection({ userId }: { userId: string }) {
  const fn = useServerFn(listAdminAuditLog);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "user-audit", userId],
    queryFn: () => fn({ data: { targetUserId: userId, limit: 25 } }),
  });

  return (
    <div>
      <p className="overline mb-2">
        Admin activity {data ? `(${data.entries.length})` : ""}
      </p>
      {isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
      {data && (
        <ul className="border border-border divide-y divide-border bg-card">
          {data.entries.map((e) => (
            <li key={e.id} className="p-3 text-xs">
              <div className="flex justify-between items-baseline gap-2">
                <span className="font-mono text-primary">{e.action}</span>
                <span className="font-mono text-ink-tertiary">
                  {new Date(e.created_at).toLocaleString("en-GB")}
                </span>
              </div>
              <p className="font-mono text-ink-secondary mt-1">{e.admin_email ?? "—"}</p>
              {e.details && (
                <pre className="font-mono text-[10px] text-ink-tertiary mt-1 whitespace-pre-wrap break-all max-h-28 overflow-y-auto">
                  {e.details}
                </pre>
              )}
            </li>
          ))}
          {data.entries.length === 0 && (
            <li className="p-4 text-center text-ink-tertiary text-sm">
              No admin actions logged for this user.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}



function UserSignalsSection({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listAccountSignals);
  const reviewFn = useServerFn(markSignalReviewed);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "user-signals", userId],
    queryFn: () => listFn({ data: { userId, limit: 20 } }),
  });

  const review = useMutation({
    mutationFn: (s: AccountSignalRow) =>
      reviewFn({ data: { id: s.id } }),
    onSuccess: () => {
      toast.success("Signal marked reviewed");
      qc.invalidateQueries({ queryKey: ["admin", "user-signals", userId] });
      qc.invalidateQueries({ queryKey: ["admin", "user-signal-counts"] });
      qc.invalidateQueries({ queryKey: ["admin", "signals"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div>
      <p className="overline mb-2">
        Integrity signals {data ? `(${data.signals.length})` : ""}
      </p>
      {isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
      {data && (
        <ul className="border border-border divide-y divide-border bg-card">
          {data.signals.map((s) => (
            <li key={s.id} className="p-3 text-sm">
              <div className="flex justify-between items-baseline gap-2">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] font-mono ${
                      s.severity >= 4
                        ? "bg-destructive text-destructive-foreground"
                        : s.severity >= 2
                          ? "bg-amber-200 text-amber-900"
                          : "bg-muted text-ink-secondary"
                    }`}
                  >
                    sev {s.severity}
                  </span>
                  <span className="font-mono text-xs text-primary">{s.signal_type}</span>
                  {s.reviewed_at && (
                    <span className="text-[10px] font-mono text-ink-tertiary">reviewed</span>
                  )}
                </div>
                <span className="text-[11px] font-mono text-ink-tertiary">
                  {new Date(s.created_at).toLocaleDateString("en-GB")}
                </span>
              </div>
              {!s.reviewed_at && (
                <button
                  type="button"
                  onClick={() => review.mutate(s)}
                  disabled={review.isPending}
                  className="mt-2 text-xs text-primary underline underline-offset-4 disabled:opacity-50"
                >
                  Mark reviewed
                </button>
              )}
            </li>
          ))}
          {data.signals.length === 0 && (
            <li className="p-4 text-center text-ink-tertiary text-sm">
              No signals recorded.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
